import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PDFViewer from "../components/UI/PDFViewer";

const NewslettersPage = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState("newsletters");
  const [isVisible, setIsVisible] = useState(false);
  const [isNewsletterModalOpen, setIsNewsletterModalOpen] = useState(false);
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sectionRef = useRef(null);
  const carouselRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const newsletters = [
    {
      id: 1,
      title: "DCA First Newsletter",
      date: "2025",
      description:
        "Stay updated with our latest project developments, milestones, and insights from the DCA project.",
      image: "/images/dca/20250423_102707 (1).jpg",
      pdfTitle: "DCA First Newsletter",
      pdfFiles: {
        en: {
          title: "DCA First Newsletter",
          file: "/pdf/DCA_newsletter_english .pdf",
          flag: "🇬🇧",
          language: "English",
        },
        ar: {
          title: "DCA First Newsletter",
          file: "/pdf/DCA_newsletter_arabic.pdf",
          flag: "🇸🇦",
          language: "العربية",
        },
        pt: {
          title: "DCA First Newsletter",
          file: "/pdf/1st DCA Newsletter PT.pdf",
          flag: "🇵🇹",
          language: "Português",
        },
        ro: {
          title: "DCA First Newsletter",
          file: "/pdf/DCA_newsletter_romain.pdf",
          flag: "🇷🇴",
          language: "Română",
        },
        pl: {
          title: "DCA First Newsletter",
          file: "/pdf/1st DCA Newsletter PL.pdf",
          flag: "🇵🇱",
          language: "Polski",
        },
      },
    },
    {
      id: 2,
      title: "DCA Second Newsletter",
      date: "2026",
      description:
        "Stay updated with our latest project developments, milestones, and insights from the DCA project.",
      image: "/images/dca/Training%20Tunisia/Final/DCA%20Post-01.jpg",
      pdfTitle: "DCA Second Newsletter",
      pdfFiles: {
        en: {
          title: "DCA Second Newsletter",
          file: "/pdf/DCA_second_newsletter/2nd DCA Newsletter EN.pdf",
          flag: "🇬🇧",
          language: "English",
        },
        ar: {
          title: "DCA Second Newsletter",
          file: "/pdf/DCA_second_newsletter/2nd DCA Newsletter AR.pdf",
          flag: "🇸🇦",
          language: "العربية",
        },
        pt: {
          title: "DCA Second Newsletter",
          file: "/pdf/DCA_second_newsletter/2nd DCA Newsletter PT.pdf",
          flag: "🇵🇹",
          language: "Português",
        },
        ro: {
          title: "DCA Second Newsletter",
          file: "/pdf/DCA_second_newsletter/2nd DCA Newsletter RO.pdf",
          flag: "🇷🇴",
          language: "Română",
        },
        pl: {
          title: "DCA Second Newsletter",
          file: "/pdf/DCA_second_newsletter/2nd DCA Newsletter PL.pdf",
          flag: "🇵🇱",
          language: "Polski",
        },
      },
    },
  ];

  const nextSlide = () => {
    if (newsletters.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % newsletters.length);
  };

  const prevSlide = () => {
    if (newsletters.length <= 1) return;
    setCurrentIndex(
      (prev) => (prev - 1 + newsletters.length) % newsletters.length
    );
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const handleCardClick = (newsletter) => {
    setSelectedNewsletter(newsletter);
    setIsNewsletterModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <Header
        activeSection={activeSection}
        setActiveSection={setActiveSection}
      />

      {/* Main Content */}
      <main className="pt-16">
        <section
          ref={sectionRef}
          className="py-20 relative overflow-hidden"
          style={{ backgroundColor: "var(--section-background)" }}
        >
          {/* Background decorations */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-green-50/30 via-transparent to-blue-50/30"></div>
            <div className="absolute top-20 right-20 w-72 h-72 bg-gradient-to-r from-green-200/20 to-emerald-200/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-r from-blue-200/20 to-indigo-200/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 md:px-8">
            {/* Section header */}
            <div
              className={`text-center mb-16 transition-all duration-1000 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
                  {t("newsletters.title", "Newsletters")}
                </span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto rounded-full mb-8"></div>
             </div>

            {/* Newsletter Carousel */}
            <div
              ref={carouselRef}
              className={`relative transition-all duration-1000 delay-300 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
            >
              <div className="relative overflow-hidden">
                {/* Cards Container */}
                <div className="flex items-center justify-center gap-8 md:px-12">
                  {newsletters.map((newsletter, index) => {
                    const isActive = index === currentIndex;
                    return (
                      <div
                        key={newsletter.id}
                        className={`flex-shrink-0 w-full max-w-md transition-all duration-500 ${
                          isActive
                            ? "opacity-100 scale-100 z-10"
                            : "opacity-50 scale-95 z-0 hidden md:block"
                        }`}
                        style={{
                          display:
                            isActive || newsletters.length === 1
                              ? "block"
                              : "none",
                        }}
                      >
                        <div
                          className="group relative bg-white rounded-3xl shadow-xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-2xl border border-emerald-100"
                          onClick={() => handleCardClick(newsletter)}
                        >
                          {/* Card Image */}
                          <div className="relative h-64 overflow-hidden">
                            <img
                              src={newsletter.image}
                              alt={newsletter.title}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              loading="lazy"
                              onError={(e) => {
                                e.target.src =
                                  "https://via.placeholder.com/400x300/57CC99/FFFFFF?text=Newsletter";
                              }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>

                            {/* Newsletter Badge */}
                            <div className="absolute top-4 right-4">
                              <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                                Newsletter
                              </span>
                            </div>
                          </div>

                          {/* Card Content */}
                          <div className="p-6">
                            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 group-hover:text-emerald-700 transition-colors duration-300 line-clamp-2">
                              {newsletter.title}
                            </h3>
                            <p className="text-sm text-emerald-600 mb-4 font-medium">
                              {newsletter.date}
                            </p>
                            <p className="text-gray-600 leading-relaxed line-clamp-3">
                              {newsletter.description}
                            </p>

                            {/* Read More Indicator */}
                            <div className="mt-6 flex items-center text-emerald-600 font-semibold group-hover:text-emerald-700 transition-colors duration-300">
                              <span className="mr-2">View Newsletter</span>
                              <svg
                                className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Navigation Arrows */}
                {newsletters.length > 1 && (
                  <>
                    <button
                      onClick={prevSlide}
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 z-20"
                      aria-label="Previous newsletter"
                    >
                      <svg
                        className="w-6 h-6 text-gray-700"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>

                    <button
                      onClick={nextSlide}
                      className="absolute right-0 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 z-20"
                      aria-label="Next newsletter"
                    >
                      <svg
                        className="w-6 h-6 text-gray-700"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </>
                )}
              </div>

              {/* Dots Indicator */}
              {newsletters.length > 1 && (
                <div className="flex justify-center space-x-2 mt-8">
                  {newsletters.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentIndex
                          ? "bg-emerald-600 "
                          : "bg-gray-300 hover:bg-emerald-400"
                      }`}
                      aria-label={`Go to newsletter ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      {/* Newsletter PDF Viewer */}
      <PDFViewer
        isOpen={isNewsletterModalOpen}
        onClose={() => {
          setIsNewsletterModalOpen(false);
          setSelectedNewsletter(null);
        }}
        title={selectedNewsletter?.pdfTitle || "DCA First Newsletter"}
        pdfFiles={selectedNewsletter?.pdfFiles}
      />

      {/* Footer */}
      <Footer
        activeSection={activeSection}
        setActiveSection={setActiveSection}
      />
    </div>
  );
};

export default NewslettersPage;
